/**
 * OTP Login Component
 *
 * Handles the multi-factor authentication (MFA) step after initial email/password login.
 * This component:
 * - Supports multiple MFA methods (email OTP, authenticator app)
 * - Manages OTP input and validation
 * - Completes the login flow with MFA verification
 * - Provides feedback during the verification process
 *
 * This is the second step in a two-step authentication process when MFA is enabled.
 *
 * @module components/auth/otp-login
 */

import { useRef, useState } from "react";
import { useDispatch } from "react-redux";
import OTPMethod from "./otp-method";
import AuthTemplate from "./auth-template";
import { login } from "../../store/slices/auth";
import { MULTI_FACTOR_TYPES } from "../strings";
import FixedLoader from "../loader/fixed-loader";
import { loginUser } from "../../services/api-declaration";
import { showError, showSuccess } from "../../services/toast-service";
import { INITITIAL_LOADING_DATA } from "../common-confgs/common-vars";

/**
 * OTP Login Component for multi-factor authentication
 *
 * @param {Object} props - Component properties
 * @param {string} props.email - User's email from first login step
 * @param {string} props.password - User's password from first login step
 * @param {Array} props.authenticators - Available authentication methods
 * @returns {JSX.Element} OTP verification interface
 */
const OTPLogin = ({ email, password, authenticators = [] }) => {
  // Loading state for feedback during authentication
  const [loadingData, setLoadingData] = useState(INITITIAL_LOADING_DATA);
  // References to OTP input fields for manipulation
  const inputsRef = useRef([]);
  const dispatch = useDispatch();

  /**
   * Handle OTP verification and complete login
   *
   * @param {Object} formValues - Form values including OTP code
   * @param {string} formValues.code - The OTP verification code
   * @param {string} formValues.otpMethod - Authentication method used (email/authenticator)
   */
  const handleLoginWithOtp = async (formValues) => {
    try {
      // Show loading state
      setLoadingData({ isLoading: true, loadingText: "Logging In.." });

      // Send login request with email, password and OTP verification
      const response = await loginUser({ ...formValues, email, password });

      // Complete login on success
      dispatch(login(response.data));
      showSuccess("Sie haben sich erfolgreich angemeldet!");
    } catch (error) {
      console.log(error);
      // Show appropriate error message
      showError(error.response?.data?.message || "Etwas ist schief gelaufen!");
    } finally {
      // Reset input fields
      inputsRef.current.forEach((input) => {
        input.disabled = false;
        input.value = "";
      });
      // Reset loading state
      setLoadingData(INITITIAL_LOADING_DATA);
    }
  };
  /**
   * Render the OTP verification interface
   */
  return (
    <div className="position-relative">
      {/* Authentication template for consistent styling */}
      <AuthTemplate className="" width={400} title="Anmelden">
        {/* OTP method selection and input component */}
        <OTPMethod
          email={email}
          onSubmit={handleLoginWithOtp}
          inputsRef={inputsRef}
          loadingData={loadingData}
          setLoadingData={setLoadingData}
          authenticators={authenticators}
          // Determine default authentication type based on available methods
          defaultAuthType={
            authenticators.length
              ? MULTI_FACTOR_TYPES.EMAIL // Use email if available
              : MULTI_FACTOR_TYPES.AUTHENTICATOR // Otherwise use authenticator
          }
        />
      </AuthTemplate>

      {/* Show loader overlay during authentication */}
      {loadingData.isLoading && loadingData.loadingText && (
        <FixedLoader loaderMessage={loadingData.loadingText} />
      )}
    </div>
  );
};

export default OTPLogin;
